import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { DialogService, MobileService, SessionStorageService } from '@vectronic/util';
import { DatatableFilterDialogComponent } from '../components/datatable-filter-dialog/datatable-filter-dialog.component';
import { DatatableConfig } from '../types/datatable-configuration';

@Injectable()
export class FilterService<T extends object> {
  private sessionStorage = inject(SessionStorageService);
  private mobileService = inject(MobileService);
  private dialogService = inject(DialogService);

  private config = signal<DatatableConfig<T>['filter']>(undefined);
  private uniqueId = signal<string>(undefined);

  loadShowFilterEffect = effect(() => {
    if (this.config()) untracked(() => this.doShow.set(this.loadShowFilter()));
  });

  set({ filter, uniqueId }: DatatableConfig<T>) {
    this.config.set(filter);
    this.uniqueId.set(uniqueId);
  }

  init(config?: DatatableConfig<T>) {
    if (config) this.set(config);
    const filters = this.config()?.filters;
    if (!filters?.length) return;
    let storedFilters: { [key: string]: any } | undefined;
    if (this.config()?.saveStateInSession) {
      const key = this.uniqueId() + '-filter-state';
      storedFilters = this.sessionStorage.getParsed(key);
    }
    const initialFilters: { [key: string]: any } = {};
    if (storedFilters) {
      Object.assign(initialFilters, storedFilters);
    } else {
      filters.forEach((filter) => {
        if (filter.defaultValue !== undefined) {
          initialFilters[filter.title] = filter.defaultValue;
        }
      });
    }
    this.activeFilters.set(initialFilters);
    filters.forEach((filter) => {
      let value = initialFilters[filter.title];
      if (
        (filter.type === 'multi-select' || filter.type === 'process-id') &&
        Array.isArray(value) &&
        value.length === 0
      ) {
        value = undefined;
      }
      if (value !== undefined) (filter.onChange as (value: any) => void)?.(value);
    });
  }

  filters = computed(() => {
    const filters = this.config()?.filters;
    if (!filters) return filters;
    return filters.map((filter) => {
      const { type, options, sortOptions } = filter;
      if (
        (type === 'multi-select' || type === 'single-select') &&
        Array.isArray(options) &&
        sortOptions !== false
      ) {
        return {
          ...filter,
          options: [...options].sort((a, b) => a.label.localeCompare(b.label)),
        };
      }
      return filter;
    });
  });

  hasFilters = computed(() => Boolean(this.filters()?.length));

  activeFilters = signal<{ [key: string]: any }>({});

  effectiveActiveFilters = computed(() =>
    mergeFilterValues(this.activeFilters(), this.config()?.extraFilterValues ?? {}),
  );

  doShow = signal(false);

  state = computed(() => {
    if (this.hasFilters())
      return {
        isCollapsed: this.doShow(),
        count:
          this.filters()?.filter(({ title }) => {
            const value = this.effectiveActiveFilters()[title];
            return value != undefined && (!Array.isArray(value) || value.length > 0);
          })?.length ?? 0,
      };
  });

  onFilterChange(event: { title: string; value: any }) {
    const current = this.activeFilters();
    const newFilters = { ...current, [event.title]: event.value };
    this.activeFilters.set(newFilters);
    const config = this.config();
    const filter = config?.filters.find((f) => f.title === event.title);
    if (
      (filter?.type === 'multi-select' || filter?.type === 'process-id') &&
      !event.value?.length
    ) {
      event.value = undefined;
    }
    (filter?.onChange as (value: any) => void)?.(event.value);
    config?.onFilterChange?.(event);
    if (config?.saveStateInSession) {
      this.sessionStorage.set(this.uniqueId() + '-filter-state', newFilters);
    }
  }

  applyFilterValues(values: { [key: string]: any } = {}) {
    const config = this.config();
    const filters = config?.filters;
    if (!filters?.length) return;

    const nextFilters = filters.reduce(
      (acc, filter) => {
        let value = values[filter.title];
        if (
          (filter.type === 'multi-select' || filter.type === 'process-id') &&
          Array.isArray(value) &&
          value.length === 0
        ) {
          value = undefined;
        }
        if (value !== undefined) acc[filter.title] = value;
        (filter.onChange as (value: any) => void)?.(value);
        return acc;
      },
      {} as { [key: string]: any },
    );

    this.activeFilters.set(nextFilters);
  }

  onFilterToggle() {
    if (!this.hasFilters()) return;
    const config = this.config();
    this.doShow.set(!this.doShow());
    this.saveShowFilter();
    if (this.mobileService.isOn()) {
      this.dialogService.open(DatatableFilterDialogComponent, {
        data: {
          filters: this.filters(),
          activeFilters: this.effectiveActiveFilters(),
          selectionFilters: config?.selectionFilters ?? [],
          selectedSelectionFilter: config?.selectedSelectionFilter,
        },
        config: { minWidth: '60vw', width: 'auto' },
        afterOpened: ({
          filterChange,
          saveSelectionFilter,
          editSelectionFilter,
          deleteSelectionFilter,
          selectionFilterChange,
        }) => {
          filterChange.subscribe((event) => this.onFilterChange(event));
          saveSelectionFilter.subscribe((payload) => config?.onSaveSelectionFilter?.(payload));
          editSelectionFilter.subscribe((payload) => config?.onEditSelectionFilter?.(payload));
          deleteSelectionFilter.subscribe((id) => config?.onDeleteSelectionFilter?.(id));
          selectionFilterChange.subscribe((id) => config?.onSelectionFilterChange?.(id));
        },
      });
    }
  }

  loadShowFilter() {
    const config = this.config();
    const uniqueId = this.uniqueId();
    if (!uniqueId) return true;
    const stored = localStorage.getItem(uniqueId + '-show-filter');
    if (stored !== null) return stored === 'true';
    if (config?.isCollapsed) {
      const activeFilters = untracked(() => this.effectiveActiveFilters());
      const hasActiveFilters = this.filters()?.some(({ title }) => {
        const value = activeFilters[title];
        return value != undefined && (!Array.isArray(value) || value.length > 0);
      });
      return Boolean(hasActiveFilters);
    }
    return true;
  }

  private saveShowFilter() {
    localStorage.setItem(this.uniqueId() + '-show-filter', String(this.doShow()));
  }

  applyFilters(data: T[]): T[] {
    const filters = this.filters()?.filter(({ filter }) => Boolean(filter));
    const activeFilters = this.effectiveActiveFilters();
    if (!filters?.length) return data;
    filters.forEach((datatableFilter) => {
      const value = activeFilters[datatableFilter.title];
      const hasValue = value != undefined && (!Array.isArray(value) || value.length > 0);
      if (hasValue) data = data.filter((row) => datatableFilter.filter(row, value));
    });
    return data;
  }
}

function mergeFilterValues(
  baseValues: { [key: string]: any },
  extraValues: { [key: string]: any },
) {
  return Object.entries(extraValues).reduce(
    (acc, [key, value]) => {
      if (value === undefined || (Array.isArray(value) && value.length === 0)) return acc;

      const baseValue = acc[key];
      if (Array.isArray(baseValue) && Array.isArray(value)) {
        acc[key] = [...new Set([...baseValue, ...value])];
      } else {
        acc[key] = value;
      }

      return acc;
    },
    { ...baseValues },
  );
}
